/** Staff — Business Overview: which parts of the business make the most money (revenue, and profit for those allowed). */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Briefcase, PoundSterling, TrendingUp, Percent, Hash, Loader2, Calendar as CalendarIcon, Lock } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { StatTile, gbp } from '@/components/training/analytics/shared';
import { BrandsCard, MonthlyChart, StreamsCard, TopProducts, STREAM_COLOURS, BRAND_COLOURS } from '@/components/overview/OverviewCards';
import { MonthlyCostsTable, OrdersTable } from '@/components/overview/OverviewTables';

const STAFF = ['admin', 'manager', 'team'];
const ALL_STREAMS = [['training', 'Training courses'], ['probound', 'ProBound quotes'], ['proepoxy', 'ProEpoxy orders'], ['prodecor', 'ProDecor orders'], ['material_order', 'Material orders'], ['sales_log', 'Sales & Profit log']];
const ALL_BRANDS = [['probound', 'ProBound'], ['proepoxy', 'ProEpoxy'], ['prodecor', 'ProDecor']];
const iso = (d) => d.toISOString().slice(0, 10);
const startOfMonthsAgo = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return iso(d); };
const endOfThisMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

function Chip({ active, label, colour, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${active ? 'text-white' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
      style={active ? { background: colour, borderColor: colour } : undefined}>{label}</button>
  );
}

export default function BusinessOverview({ profitMode = false, embedded = false }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [start, setStart] = useState(startOfMonthsAgo(5));
  const [end, setEnd] = useState(endOfThisMonth());
  const [streams, setStreams] = useState(ALL_STREAMS.map(([k]) => k));
  const [brands, setBrands] = useState(ALL_BRANDS.map(([k]) => k));
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => { if (user && !STAFF.includes(user.role)) navigate('/', { replace: true }); }, [user, navigate]);

  const run = useCallback(async (quiet = false) => {
    if (!start || !end) { toast.error('Pick both dates first.'); return; }
    if (!quiet) setBusy(true);
    try {
      const qs = new URLSearchParams({ start_date: start, end_date: end, streams: streams.join(','), brands: brands.join(','), ...(profitMode ? { profit: '1' } : {}) });
      const res = await fetch(`${API_URL}/api/staff/business-overview?${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Report failed');
      setData(await res.json());
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [start, end, streams, brands]);

  useEffect(() => { if (user && STAFF.includes(user.role)) run(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, streams, brands]);

  const toggle = (list, setList, key) => setList((l) => (l.includes(key) ? (l.length > 1 ? l.filter((k) => k !== key) : l) : [...l, key]));
  const presets = [
    { label: 'This month', s: startOfMonthsAgo(0), e: endOfThisMonth() }, { label: 'Last 3 months', s: startOfMonthsAgo(2), e: endOfThisMonth() },
    { label: 'Last 6 months', s: startOfMonthsAgo(5), e: endOfThisMonth() }, { label: 'Last 12 months', s: startOfMonthsAgo(11), e: endOfThisMonth() },
    { label: 'This year', s: `${new Date().getFullYear()}-01-01`, e: `${new Date().getFullYear()}-12-31` },
  ];
  const t = data?.totals;

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-to-b from-slate-100 to-white'}>
      {!embedded && <div className="border-b sticky top-0 z-20 shadow-sm bg-[#1f2937] text-white">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff" className="text-white/80 hover:text-white" data-testid="business-overview-back"><ArrowLeft className="w-5 h-5" /></Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex"><Home className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><Briefcase className="w-4 h-4" /></div>
          <h1 className="text-base sm:text-lg font-bold truncate flex-1">Business Overview — sales</h1>
          <Link to="/staff/training-analytics" className="hidden md:inline text-xs text-white/80 hover:text-white underline shrink-0" data-testid="training-analytics-link">Training analytics</Link>
          <Link to="/staff/sales-profit" className="hidden md:inline text-xs text-white/80 hover:text-white underline shrink-0" data-testid="sales-profit-link">Sales Log</Link>
        </div>
      </div>}

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <Card className="p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[9rem]"><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="overview-start" /></div>
            <div className="flex-1 min-w-[9rem]"><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="overview-end" /></div>
            <Button onClick={() => run()} disabled={busy} className="bg-[#1f2937] hover:bg-black text-white font-bold" data-testid="overview-run">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-1.5" />}{busy ? 'Loading…' : 'Run'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => <button key={p.label} type="button" onClick={() => { setStart(p.s); setEnd(p.e); }} className="text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid={`overview-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>{p.label}</button>)}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t">
            <span className="text-[11px] uppercase tracking-wider text-gray-500 mr-1">Parts of the business</span>
            {ALL_STREAMS.map(([k, l]) => <Chip key={k} active={streams.includes(k)} label={l} colour={STREAM_COLOURS[k]} onClick={() => toggle(streams, setStreams, k)} testid={`filter-stream-${k}`} />)}
            <button type="button" onClick={() => setStreams(ALL_STREAMS.map(([k]) => k))} className="text-[11px] text-gray-500 underline ml-1" data-testid="filter-stream-all">all</button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider text-gray-500 mr-1">Brand</span>
            {ALL_BRANDS.map(([k, l]) => <Chip key={k} active={brands.includes(k)} label={l} colour={BRAND_COLOURS[k]} onClick={() => toggle(brands, setBrands, k)} testid={`filter-brand-${k}`} />)}
            <button type="button" onClick={() => setBrands(ALL_BRANDS.map(([k]) => k))} className="text-[11px] text-gray-500 underline ml-1" data-testid="filter-brand-all">all</button>
          </div>
        </Card>

        {data && (
          <>
            <div className={`grid grid-cols-2 gap-3 ${data.can_view_profit ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
              <StatTile icon={PoundSterling} label="Revenue (ex VAT)" value={gbp(t.revenue)} sub={`${t.count} sales · ${data.streams.filter((s) => s.revenue > 0).length} parts of the business`} tone="good" testid="stat-revenue" />
              <StatTile icon={Hash} label="Biggest earner" value={data.streams[0]?.revenue ? data.streams[0].label : '—'} sub={data.streams[0]?.revenue ? `${data.streams[0].share}% of revenue` : 'No sales in range'} tone="info" testid="stat-top-stream" />
              {data.can_view_profit && (
                <>
                  <StatTile icon={TrendingUp} label="Profit" value={`${t.profit < 0 ? '−' : ''}${gbp(Math.abs(t.profit))}`} sub={`Costs ${gbp(t.cost)}`} tone={t.profit >= 0 ? 'good' : 'bad'} testid="stat-profit" />
                  <StatTile icon={Percent} label="Margin" value={t.margin_pct === null ? '—' : `${t.margin_pct}%`} sub="Profit ÷ revenue" tone="default" testid="stat-margin" />
                </>
              )}
            </div>
            {!data.can_view_profit && (
              <p className="text-[11px] text-gray-500 flex items-center gap-1" data-testid="profit-locked-note"><Lock className="w-3 h-3" /> Revenue only — costs and profit live in the Profit Centre.</p>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><StreamsCard data={data} /><BrandsCard data={data} /></div>
            <MonthlyChart data={data} />
            {data.can_view_profit && <MonthlyCostsTable data={data} reload={() => run(true)} />}
            <TopProducts rows={data.top_products} />
            <OrdersTable data={data} reload={() => run(true)} />
          </>
        )}
      </div>
    </div>
  );
}
